import type { CenterWithCounts } from '@api/types';
import * as Icons from '@components/Icons';

interface Props {
  readonly center: CenterWithCounts;
  readonly onToggle: (next: boolean) => void;
  readonly onOpen: () => void;
  readonly toggleDisabled?: boolean;
}

/** Derive a representative "HH:MM-HH:MM" from the centre's business hours.
 *  Returns "—" when no schedule is configured, "Cerrado" when every day is
 *  null, or the range of the first open day otherwise. Matches the compact
 *  format the mockup shows (the full per-day grid lives in the drawer). */
function formatHours(bh: CenterWithCounts['businessHours']): string {
  if (!bh) return '—';
  const first = bh.find((d) => d !== null);
  if (!first) return 'Cerrado';
  return `${first.open}-${first.close}`;
}

export function CenterCard({ center, onToggle, onOpen, toggleDisabled }: Props) {
  const isActive = center.isActive;
  const isManual = center.confirmationMode === 'manual';
  const addressLine = [center.address, center.city].filter(Boolean).join(' · ') || '—';
  const hours = formatHours(center.businessHours);

  return (
    <button
      type="button"
      className="center-card"
      onClick={onOpen}
      data-testid={`center-card-${center.id}`}
    >
      <div className="center-card-banner" aria-hidden="true" />
      <div className="center-card-pin">
        <Icons.Pin size={14} />
      </div>

      <div className="center-card-head">
        <div className="center-card-title">{center.name}</div>
        <button
          type="button"
          className="switch"
          role="switch"
          aria-checked={isActive}
          aria-label={isActive ? 'Desactivar centro' : 'Activar centro'}
          disabled={toggleDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(!isActive);
          }}
        >
          <span className="switch-track" data-on={isActive ? 'true' : 'false'}>
            <span className="switch-thumb" />
          </span>
        </button>
      </div>

      <div className="center-card-pills">
        <span className="pill" data-tone={isActive ? 'ok' : 'neutral'}>
          {isActive ? 'ACTIVO' : 'INACTIVO'}
        </span>
        <span className="pill" data-tone={isManual ? 'warn' : 'info'}>
          {isManual ? 'MANUAL' : 'AUTO'}
        </span>
      </div>

      <div className="center-card-address">
        {addressLine}
        <div className="center-card-address-sub">
          {hours}
        </div>
      </div>

      <div className="center-card-stats">
        <div>
          <div className="center-card-stat-label">SALAS</div>
          <div className="center-card-stat-value mono">{center.roomsCount}</div>
          {!center.roomsAffectAvailability && center.roomsCount > 0 ? (
            <div className="center-card-stat-note">sin uso</div>
          ) : null}
        </div>
        <div>
          <div className="center-card-stat-label">STAFF</div>
          <div className="center-card-stat-value mono">{center.staffCount}</div>
        </div>
        <div>
          <div className="center-card-stat-label">RESERVAS/MES</div>
          <div className="center-card-stat-value mono">{center.bookingsLast30d}</div>
        </div>
      </div>
    </button>
  );
}
